import { memo, useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { get, keyBy, mapValues, size } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import { Menu, Paper } from "@mui/material"
import { AccountCircle, FilterNone, Star, People, Person, ExitToApp, SyncAlt, FilterDrama, Settings, PhotoSizeSelectActual, RotateLeft, Webhook, SpaceDashboardOutlined, AttachFile, AutoAwesomeOutlined, AutoAwesome } from "@mui/icons-material"
import Search from "modules/home-search"
import { getIsDashboardEnabled, getIsManagedAgentEnabled } from "selectors/features"
import { useAccountId, useIsAiTermsAccepted } from "resources/account"
import { useModels } from "resources/models"
import { useIsDefinedRouterMatchProp } from "hooks/router"
import { getIsSuperuser, getRole } from "helpers/permissions/roles"
import { getHasAgenticWorkflows, getHasSolutions, getIsSuperuserSession } from "modules/home-layout/selectors"
import { Creators } from "modules/home-layout/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { KellyNudge } from "connected-components/agent-chat/components/kelly-nudge"
import Item from "./components/item"
import SearchButton from "./components/search-button"
import SettingsMenuItem from "./components/settings-menu-item"
import { useSolutionsCount } from "./use-solutions-count"

import "./style.scss"

// Hoisted so the memoized `Item` sees a stable element; inline JSX would be a new prop every render.
// Space before <br/> keeps the accessible name "Agentic Tasks" (not "AgenticTasks"); <br> adds no whitespace to textContent.
const AGENTIC_TASKS_LABEL = <>Agentic <br />Tasks</>

// Analytics keys, deliberately decoupled from route names (see "agent-chat" below).
const TRACKED_ITEMS = ["dashboard", "agent-chat", "market-tools", "favorites", "resources",
  "data-cloud", "platform", "solutions", "ai"]

// Must clear `$z-idx-highest` (10000) in `styles/z-indexes.scss`, which legacy overlays sit on.
const SETTINGS_MENU_Z_INDEX = 20000

const getMultiAccounts = state => get(state, "user.accounts.length", 0) > 1
const getHeaderModelId = state => get(state, "modules.homeLayout.header.modelId")

function GlobalNavRail({ searchModelId }) {
  const dispatch = useDispatch()
  const accountId = useAccountId()
  const [models] = useModels()
  useSolutionsCount()

  const role = useSelector(getRole)
  const isSuperuser = useSelector(getIsSuperuser)
  const isSuperuserSession = useSelector(getIsSuperuserSession)
  const multiAccounts = useSelector(getMultiAccounts)
  const hasSolutions = useSelector(getHasSolutions)
  const hasAgenticWorkflows = useSelector(getHasAgenticWorkflows)
  const isDashboardEnabled = useSelector(getIsDashboardEnabled)
  const isManagedAgentEnabled = useSelector(getIsManagedAgentEnabled)
  const isAiTermsAccepted = useIsAiTermsAccepted()
  const headerModelId = useSelector(getHeaderModelId)
  // The app header slot is never cleared on leaving home, so shells outside home must name their own model.
  // Absent prop means home; a null one means a shell whose model hasn't resolved yet — pass that through
  // rather than coalescing, or the stale header value would beat the search's own mostUsedOrDefault.
  const searchModel = searchModelId === undefined ? headerModelId : searchModelId

  const noModelSelector = useIsDefinedRouterMatchProp("noModelSelector")

  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState(null)
  const [kellyNavAnchorEl, setKellyNavAnchorEl] = useState(null)

  const openCompareSearch = useCallback(ev => {
    dispatch(Creators.trackSearchIcon())
    ev.currentTarget.blur()
    dispatch(DialogCreators.open({
      content: <Search modelId={searchModel} disableModel={noModelSelector} />,
    }))
  }, [dispatch, searchModel, noModelSelector])

  // One stable handler per item, built once — `getTrackMenu(type)` in render handed every item a
  // fresh closure on each pass, so no child could ever memoize.
  const trackMenu = useMemo(
    () => mapValues(keyBy(TRACKED_ITEMS), item => () => dispatch(Creators.trackMenu(item))),
    [dispatch],
  )

  const toggleSettingsMenu = useCallback(ev => {
    const nextAnchorEl = !settingsMenuAnchorEl ? ev.currentTarget : null
    setSettingsMenuAnchorEl(nextAnchorEl)
    dispatch(Creators.toggleSettingsMenu(!!nextAnchorEl))
  }, [dispatch, settingsMenuAnchorEl])

  return <>
    <Paper square className="global-nav-rail nav-bar">
      <div className="top">
        <SearchButton noModels={!size(models)} onClick={openCompareSearch} />
        {isDashboardEnabled && <Item label="Dashboard" MaterialIcon={SpaceDashboardOutlined} to="dashboard" onClick={trackMenu["dashboard"]} showBetaTag />}
        {/* Analytics key stays "agent-chat" though the route is now "kelly-ai": it's the stable event name
            existing dashboards/funnels filter on, not the route — renaming it would split the metric history. */}
        {isManagedAgentEnabled && isAiTermsAccepted && <>
          <Item label="Kelly AI" MaterialIcon={AutoAwesome} to="kelly-ai" onClick={trackMenu["agent-chat"]} showBetaTag ref={setKellyNavAnchorEl} />
          <KellyNudge anchorEl={kellyNavAnchorEl} />
        </>}
        <Item label="Market" MaterialIcon={PhotoSizeSelectActual} to="home/market" onClick={trackMenu["market-tools"]} disabled={!size(models)} disabledTooltip="Market Tools is disabled since you do not have access to any risk model" />
        <Item label="Favorites" MaterialIcon={Star} to="home/favorites" onClick={trackMenu["favorites"]} />
        <Item label="Resources" MaterialIcon={FilterNone} to="home/resources" onClick={trackMenu["resources"]} />
        <Item label="Data" MaterialIcon={FilterDrama} to="home/data-cloud" onClick={trackMenu["data-cloud"]} />
        {(isSuperuser || role === "admin") && <Item label="Platform" MaterialIcon={Webhook} to="home/platform" onClick={trackMenu["platform"]} />}
        {(isSuperuser || hasSolutions) && <Item label="Solutions" MaterialIcon={AttachFile} to="home/solutions" onClick={trackMenu["solutions"]} />}
        {/* No isSuperuser bypass here (unlike Solutions): the /home/ai route is gated on the
            skills_delivery flag, so a superuser without the flag would only get bounced back to
            /home. hasAgenticWorkflows already covers the flag, so showing it only when it's true
            keeps the nav in sync with what the route will actually render. */}
        {hasAgenticWorkflows && !(isManagedAgentEnabled && isAiTermsAccepted) && <Item label={AGENTIC_TASKS_LABEL} MaterialIcon={AutoAwesomeOutlined} to="home/ai" onClick={trackMenu["ai"]} />}
      </div>
      <div className="bottom">
        <Item label="Account" className="settings-menu-button" onClick={toggleSettingsMenu} MaterialIcon={AccountCircle} />
      </div>
    </Paper>
    <Menu
      anchorEl={settingsMenuAnchorEl}
      open={!!settingsMenuAnchorEl}
      onClose={toggleSettingsMenu}
      classes={{ paper: "home-settings-menu" }}
      sx={{ zIndex: SETTINGS_MENU_Z_INDEX }}
    >
      {(role === "admin") && <SettingsMenuItem className="account-settings-item" label="Account Settings" Icon={Settings} to="home/account" closeMenu={toggleSettingsMenu} />}
      <SettingsMenuItem className="members-item" activeUrls={["teams", "members"]} label="Teams & Members" Icon={People} to="home/members" closeMenu={toggleSettingsMenu} />
      <SettingsMenuItem label="My Profile" className="my-profile-item" Icon={Person} to="home/profile" closeMenu={toggleSettingsMenu} />
      {isSuperuser && <SettingsMenuItem className="admin-item" label="Admin" Icon={SyncAlt} to="/admin" closeMenu={toggleSettingsMenu} />}
      {multiAccounts && <SettingsMenuItem className="accounts-item" label="Accounts" Icon={SyncAlt} to="/accounts" closeMenu={toggleSettingsMenu} />}
      {isSuperuserSession && <SettingsMenuItem className="restore-session" label="Reset Session" Icon={RotateLeft} to={`/resetSession?accountId=${accountId}&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} closeMenu={toggleSettingsMenu} />}
      <SettingsMenuItem className="logout-item" label="Logout" Icon={ExitToApp} to="/logout" closeMenu={toggleSettingsMenu} />
    </Menu>
  </>
}

GlobalNavRail.propTypes = {
  searchModelId: PropTypes.string,
}

// `PortfolioRoot` re-renders on every keystroke in its search box (`searchValue` is in its
// `mapStateToProps`), and the rail's only prop is a string — so a plain memo covers it.
export default memo(GlobalNavRail)
